import React, { useState, useRef, useEffect } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Send } from 'lucide-react';
import { ScenarioComment } from '../../types/scenarios';
import { formatDistanceToNow } from 'date-fns';
import { useFormatting } from '@/hooks/useFormatting';
import { UserAvatar } from '@/components/ui/user-avatar';
import { useUserPreferences } from '@/contexts/UserPreferencesContext';

interface ChatDiscussionProps {
  comments: ScenarioComment[];
  currentUserId: string;
  currentUserName: string;
  onPostComment: (text: string) => void;
  readOnly?: boolean;
}



export const ChatDiscussion: React.FC<ChatDiscussionProps> = ({
  comments,
  currentUserId,
  currentUserName,
  onPostComment,
  readOnly = false,
}) => {
  const { formatDate } = useFormatting();
  const { getAvatarUrlByName } = useUserPreferences();
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Helper function to format timestamp
  const formatTimestamp = (date: Date): string => {
    const now = new Date();
    const messageDate = new Date(date);
    const diffInHours = (now.getTime() - messageDate.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return `Today ${messageDate.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffInHours < 48) {
      return `Yesterday ${messageDate.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return formatDate(messageDate, { 
        day: 'numeric', 
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [comments]);

  const handleSend = () => {
    if (newMessage.trim()) {
      onPostComment(newMessage.trim());
      setNewMessage('');
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Auto-resize textarea
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewMessage(e.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  return (
    <div className="flex flex-col h-[600px] bg-gray-50 rounded-lg border">
      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {comments.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
            </div>
            <p className="text-lg font-medium text-gray-900 mb-1">No messages yet</p>
            <p className="text-sm text-gray-500">Start the discussion!</p>
          </div>
        ) : (
          <>
            {comments.map((comment) => {
              const isCurrentUser = comment.userId === currentUserId;

              return (
                <div
                  key={comment.id}
                  className={`flex gap-3 ${isCurrentUser ? 'flex-row-reverse' : 'flex-row'}`}
                >
                  {/* Avatar */}
                  <div className="flex-shrink-0">
                    <UserAvatar
                      name={comment.authorName}
                      seed={comment.authorName}
                      avatarUrl={getAvatarUrlByName(comment.authorName)}
                      size="md"
                    />
                  </div>

                  {/* Message Content */}
                  <div className={`flex flex-col max-w-[70%] ${isCurrentUser ? 'items-end' : 'items-start'}`}>
                    {/* Name and Timestamp */}
                    <div className={`flex items-center gap-2 mb-1 text-xs text-gray-600 ${isCurrentUser ? 'flex-row-reverse' : 'flex-row'}`}>
                      <span className="font-medium">
                        {isCurrentUser ? `You (${currentUserName})` : comment.authorName}
                      </span>
                      <span className="text-gray-400">
                        {formatTimestamp(comment.createdAt)}
                      </span>
                    </div>

                    {/* Message Bubble */}
                    <div
                      className={`rounded-2xl px-4 py-2.5 ${
                        isCurrentUser
                          ? 'text-white rounded-tr-sm'
                          : 'bg-white text-gray-900 rounded-tl-sm border border-gray-200'
                      }`}
                      style={isCurrentUser ? { backgroundColor: 'var(--accent-teal-500)' } : undefined}
                    >
                      <p className="text-sm whitespace-pre-wrap break-words">{comment.text}</p>
                    </div>

                    {/* Role Badge (optional) */}
                    {comment.userRole && (
                      <div className="mt-1 text-xs text-gray-500">
                        {comment.userRole}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      {readOnly ? (
        <div className="border-t bg-gray-50 p-4 text-center">
          <p className="text-sm text-gray-500">You have view-only access to this discussion.</p>
        </div>
      ) : (
        <div className="border-t bg-white p-4">
          <div className="flex gap-2 items-end">
            {/* Text Input */}
            <Textarea
              ref={textareaRef}
              placeholder="Type your message..."
              value={newMessage}
              onChange={handleTextareaChange}
              onKeyPress={handleKeyPress}
              className="resize-none min-h-[44px] max-h-[120px] flex-1"
              rows={1}
            />

            {/* Send Button */}
            <Button
              onClick={handleSend}
              disabled={!newMessage.trim()}
              className="flex-shrink-0 h-10 px-4"
            >
              <Send className="h-4 w-4 mr-2" />
              Send
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      )}
    </div>
  );
};
